import { CUSTOM_FONTS, RED_HAT_FONT } from '@/components/unlayer/constants/custom-fonts';
import { deleteLogoDependency, createLogoDependency } from '@/api/campaigns/campaignUnlayerEmail';
import sentry from '@/analytics/sentry';

/* istanbul ignore next */
export const loadScript = () => {
    const SCRIPT_URL = '//editor.unlayer.com/embed.js';
    const SCRIPT_ID = 'unlayer-js-api';

    return new Promise((resolve, reject) => {
        if (document.getElementById(SCRIPT_ID) == null) {
            const script = document.createElement('script');

            script.setAttribute('src', SCRIPT_URL);
            script.setAttribute('id', SCRIPT_ID);

            script.onload = resolve;
            script.onerror = reject;

            document.head.appendChild(script);
        } else {
            resolve();
        }
    });
};

const toMergeTags = (mergeFields = []) => {
    const mergeTags = {};

    mergeFields.forEach((category) => {
        mergeTags[category.label] = {
            name: category.label,
            mergeTags: {},
        };

        category.fields.forEach(({ label, value }) => {
            const mergeCategory = mergeTags[category.label].mergeTags;

            mergeCategory[label] = {
                name: label,
                value,
            };
        });
    });

    return mergeTags;
};

// TODO: Refactor bookingLink getter into a helper function rather than a vuex
// getter.
const toSpecialLinks = ({ appointmentLinks }, appointmentTypes = [], bookingLinkHostname) => {
    const specialLinks = [];

    if (appointmentTypes.length > 0) {
        const appointmentSpecialLinks = {
            name: appointmentLinks,
            specialLinks: [
                ...appointmentTypes.map(({ title, bookingLink }) => ({
                    name: title,
                    href: `${bookingLinkHostname}/${bookingLink}`,
                    target: '_blank',
                })),
            ],
        };

        specialLinks.push(appointmentSpecialLinks);
    }

    return specialLinks;
};

export const createBaseOptions = ({
    i18n, mergeFields, bookingLinkHostname, appointmentTypes, isMaxClassic,
}) => ({
    locale: navigator.language || 'en-US',
    appearance: {
        loader: {
            html: '<div class="spinner"></div>',
            css: `
@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.spinner {
    border-radius: 50%;
    display: inline-block;
    animation: spin 1s infinite linear;
    width: 40px;
    height: 40px;
    border: solid 3px;
    border-color: #0042db;
    border-left-color: transparent;
    position: absolute;
    top: calc(50% - 20px);
    left: calc(50% - 20px);
}`,
        },
    },
    editor: {
        autoSelectOnDrop: true,
    },
    fonts: {
        showDefaultFonts: true,
        customFonts: getCustomFonts(isMaxClassic),
    },
    designTagsConfig: {
        // If left at default values, these designTags delimiters will default to [[ and ]]
        // which collides with our liquid-based merge fields. Upon loading, Unlayer
        // will replace all merge tags using [[ ]] with an empty string.
        // https://docs.unlayer.com/docs/design-tags#changing-delimiter
        delimiter: ['(╯°□°)╯︵┻━┻', '┬─┬ノ(°_°ノ)'],
    },
    mergeTags: toMergeTags(mergeFields),
    specialLinks: toSpecialLinks(i18n, appointmentTypes, bookingLinkHostname),

    // TODO: Are there any good ways of having webpack include css generated from a scss file,
    // so we can use existing design system variables, etc?
    customCSS: [
        // Make Unlayer draggable tools look like Keap cards
        `
.blockbuilder-content-tools .blockbuilder-content-tool {
    border-color: transparent;
    box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 2px 1px -1px rgba(0, 0, 0, 0.12), 0px 1px 3px rgba(0, 0, 0, 0.2);
}

.blockbuilder-content-tools .blockbuilder-content-tool:hover {
    box-shadow: 0px 4px 5px rgba(0, 0, 0, 0.14), 0px 1px 10px rgba(0, 0, 0, 0.12), 0px 2px 4px -1px rgba(0, 0, 0, 0.2);
}

.blockbuilder-content-tools .blockbuilder-content-tool.disabled {
    background-color: #f0f0f0;
    opacity: 1;
    box-shadow: inset 0px 0px 0px 1px rgba(0, 0, 0, 0.12);
}

.blockbuilder-content-tools .blockbuilder-content-tool.disabled > div {
    opacity: .5;
}

.blockbuilder-content-tools .blockbuilder-content-tool.disabled:hover {
    box-shadow: inset 0px 0px 0px 1px rgba(0, 0, 0, 0.12);
}`,
        // Normalize icon sizing so custom icons are consistent with default icons
        `
.blockbuilder-content-tool-icon {
    max-height: 33px;
}

.blockbuilder-content-tool-icon > img {
    max-height: 33px;
}`,
        // Normalize draggable content row elevation w/ cards.
        `
div[draggable="true"] > .blockbuilder-row-tool:hover {
    box-shadow: 0px 4px 5px rgba(0, 0, 0, 0.14), 0px 1px 10px rgba(0, 0, 0, 0.12), 0px 2px 4px -1px rgba(0, 0, 0, 0.2);
}`,
        // Tool name overflow fix
        `
.blockbuilder-content-tool-name {
    overflow: hidden !important;
    text-overflow: ellipsis;
    text-wrap: nowrap;
}
.blockbuilder-content-tool-name > span {
    position: relative !important;
}
`,
    ],
    tools: {
        image: {
            properties: {
                src: {
                    value: {
                        url: '',
                    },
                    editor: {
                        widgetParams: {
                            placeholderUrl: 'https://storage.googleapis.com/is-unlayer-tools.appspot.com/builds/images/logo_placeholder.svg',
                        },
                    },
                },
            },
        },
    }
});

export function cleanDependenciesFromDesign(templateId, data) {
    const {design, brandLogos, logoDependencies} = data
    let designUpdated = false;
    const validLogoIds = new Set(brandLogos.map((logo) => logo.id));
    const dependencyMap = new Map(logoDependencies.map((dep) => [dep.dependencyKey, dep]));

    for (const row of design.body.rows) {
        for (const column of row.columns) {
            for (const content of column.contents) {
                if (content.slug === 'keap#logo_tool' && content.values?.logoTool?.dependencyKey) {
                    const { dependencyKey } = content.values.logoTool || {};
                    const dep = dependencyMap.get(dependencyKey);
                    const logoExists = dep && validLogoIds.has(dep.dependencyId);

                    if (!logoExists && dependencyKey) {
                        deleteLogoDependency(templateId, dependencyKey);
                        content.values.logoTool.dependencyKey = '';
                        designUpdated = true;
                    }
                }
            }
        }
    }

    return designUpdated
}

export function updateLogoDependenciesForDuplicatedRow(design, targetId, data) {
    const { brandLogos, logoDependencies, templateId } = data;
    const promises = [];
    let rowId = null;

    /* eslint-disable no-labels */
    rowLoop:
    for (const row of design.body.rows) {
        if (row.id === targetId) {
            rowId = row.id;
            break;
        }

        for (const column of row.columns) {
            if (column.id === targetId) {
                rowId = row.id;
                break rowLoop;
            }

            for (const content of column.contents) {
                if (content.id === targetId) {
                    rowId = row.id;
                    break rowLoop;
                }
            }
        }
    }

    if (!rowId) {
        return false;
    }

    for (const row of design.body.rows) {
        if (row.id === rowId) {
            for (const column of row.columns) {
                for (const content of column.contents) {
                    if (
                        content.slug === 'keap#logo_tool'
                        && content.values?.logoTool?.dependencyKey
                    ) {
                        const logoId = logoDependencies.find(
                            (d) => d.dependencyKey
                                === content.values.logoTool.dependencyKey
                        )?.dependencyId;

                        if (logoId) {
                            const logo = brandLogos.find((l) => l.id === logoId );

                            promises.push(
                                (async () => {
                                    try {
                                        const resp = await createLogoDependency(
                                            templateId,
                                            logoId,
                                            logo?.primary || false
                                        );

                                        content.values.logoTool.dependencyKey = resp.dependencyKey;
                                    } catch (e) {
                                        sentry.captureException(e);
                                        content.values.logoTool.dependencyKey = '';
                                    }
                                })()
                            );
                        }
                    }
                }
            }

            break;
        }
    }

     if (!promises.length) {
        return false;
    }

    return Promise.all(promises);
}

export const getCustomFonts = (isMaxClassic = false) => {
    if (!isMaxClassic) {
        return [...CUSTOM_FONTS, RED_HAT_FONT];
    }

    return [...CUSTOM_FONTS];
};

export function updateLogoDependenciesForDuplicatedContent(design, id, data) {
    const { brandLogos, logoDependencies, templateId } = data;
    const promises = [];

    for (const row of design.body.rows) {
        for (const col of row.columns) {
            for (const content of col.contents) {
                if (
                    content.type === 'custom'
                    && content.slug === 'keap#logo_tool'
                    && content.id === id
                    && content?.values?.logoTool?.dependencyKey
                ) {
                    const logoId = logoDependencies.find(
                        (d) => d.dependencyKey
                            === content.values.logoTool.dependencyKey
                    )?.dependencyId;

                     if (logoId) {
                         const logo = brandLogos.find((l) => l.id === logoId);

                         promises.push(
                             (async () => {
                                 try {
                                     const resp = await createLogoDependency(
                                         templateId,
                                         logoId,
                                         logo?.primary || false
                                     );

                                     content.values.logoTool.dependencyKey = resp.dependencyKey;
                                 } catch (e) {
                                     sentry.captureException(e);
                                     content.values.logoTool.dependencyKey = '';
                                 }
                             })()
                         );
                     }
                }
            }
        }
    }

    if (!promises.length) {
        return false;
    }

    return Promise.all(promises);
}
